import { useCallback, useEffect, useMemo, useState } from "react";
import { Devices } from "@app/shared/src/api/api.gen";
import { Device } from "@app/shared/src/models";

/**
 * Complete device records for a roster, fetched one by one.
 *
 * The org roster deliberately travels light — polled every minute, it omits the sensor config with
 * its inlined calibration and tracker JSON — so anything judging radar settings off roster copies
 * reads blanks: no truck ever "follows" a radar preset, and a drift table flags transmitters that
 * are actually on. Everything radar-shaped must read these full copies instead, and the page fetches
 * them once here rather than each section fanning out its own set. Keyed on the id set, not the
 * array identity, so the minute-poll's fresh roster doesn't re-fan-out N requests.
 */
export function useFullDevices(devices: Device[]): {
    /** Undefined while the fan-out is in flight; then every record that loaded. */
    fullDevices?: Device[];
    /** Units whose full record could not be fetched — surfaced so a partial API hiccup cannot read as "saved everywhere" or "follows the default". */
    unreachable: number;
    /** Refetch in place — after a push, the copies just written are stale by exactly what was written. */
    reload: () => Promise<void>;
} {
    const [fullDevices, setFullDevices] = useState<Device[]>();
    const [unreachable, setUnreachable] = useState(0);

    const deviceIdKey = useMemo(
        () =>
            devices
                .map((d) => d.id)
                .filter((id): id is string => Boolean(id))
                .sort()
                .join(","),
        [devices],
    );

    const load = useCallback(async () => {
        const ids = deviceIdKey ? deviceIdKey.split(",") : [];
        const full = await Promise.all(ids.map((id) => Devices.getById(id).catch(() => undefined)));
        const loaded = full.filter((d): d is Device => Boolean(d));
        return { loaded, missing: ids.length - loaded.length };
    }, [deviceIdKey]);

    useEffect(() => {
        let cancelled = false;
        setFullDevices(undefined);
        setUnreachable(0);
        load().then(({ loaded, missing }) => {
            if (cancelled) return;
            setFullDevices(loaded);
            setUnreachable(missing);
        });
        return () => {
            cancelled = true;
        };
    }, [load]);

    const reload = useCallback(async () => {
        const { loaded, missing } = await load();
        setFullDevices(loaded);
        setUnreachable(missing);
    }, [load]);

    return { fullDevices, unreachable, reload };
}
